"use client";

import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";

type ToastType = "success" | "error" | "info";

interface Toast {
  id: string;
  type: ToastType;
  message: string;
}

interface ToastContextValue {
  toast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const toast = useCallback((message: string, type: ToastType = "success") => {
    const id = `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    setToasts((prev) => [...prev, { id, type, message }]);

    // Auto-dismiss after 4s
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}

      {/* Toast container — top-right.
          Refresh: ink-on-white card with a thin colored bar on the left edge
          for variant. Replaces the cheap blue/green/red flat tinted blocks
          with a calmer, more premium look that aligns with the editor v2
          design system. */}
      <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
        {toasts.map((t) => {
          const accent = t.type === "error" ? "bg-rose-500" : t.type === "info" ? "bg-brand-500" : "bg-emerald-500";
          const iconWrap = t.type === "error" ? "bg-rose-50 text-rose-600" : t.type === "info" ? "bg-brand-50 text-brand-700" : "bg-emerald-50 text-emerald-600";
          return (
            <div
              key={t.id}
              onClick={() => dismiss(t.id)}
              role="status"
              className="pointer-events-auto group relative flex cursor-pointer items-start gap-3 overflow-hidden rounded-xl border border-slate-200 bg-white px-4 py-3 pr-3 text-ink shadow-[0_8px_24px_rgba(15,23,42,0.08)] backdrop-blur-sm animate-slide-in-toast min-w-[280px] max-w-[400px]"
            >
              <span aria-hidden className={`absolute left-0 top-0 h-full w-1 ${accent}`} />
              <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-full ${iconWrap}`}>
                {t.type === "success" && (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                )}
                {t.type === "error" && (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" /></svg>
                )}
                {t.type === "info" && (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" /></svg>
                )}
              </span>
              <p className="flex-1 pt-0.5 text-[13px] font-medium leading-snug">{t.message}</p>
              <span aria-hidden className="mt-0.5 text-slate-300 transition-colors group-hover:text-slate-500">
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </span>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}
